import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import './SolutionChildPage.css';
import './SolutionAgentsPage.css';
import SolutionsWorkflowStepper from '../components/SolutionsWorkflowStepper';

const CANONICAL_URL = 'https://circumvent.io/solutions/agents';
const PAGE_TITLE = 'Investigation and remediation agents (CIRA) | Circumvent';
const PAGE_DESCRIPTION =
  'Circumvent Investigation and Remediation Agents (CIRA) investigate Cloud and AppSec findings. 95% noise reduction; humans and policy accept residual risk.';
const CIRA_HERO_ILLUSTRATION = '/images/features/icon_CIRA-hero.png';
const NEXT_STEPS_BACKGROUND = `${process.env.PUBLIC_URL}/images/backgrounds/gradient-multi-bg.webp`;
const NEXT_STEPS_SECTION_STYLE = {
  '--cta-bg-image': `url(${NEXT_STEPS_BACKGROUND})`,
};
const TRIAGE_LOG_VIDEO_SRC = 'https://circumvent-public.s3.amazonaws.com/videos/agents-triage-log.mp4';

const WORKFLOW_STAGES = [
  {
    id: 'finding',
    caption: 'Finding received',
    title: 'Finding received',
    mono: 'ingest',
    body:
      'Correlated and prioritized finding arrives from the correlation agent. CIRA picks it up from the queue and routes it to the triage agent.',
    metric: 'CVE-2024-7118 · HIGH',
    sub: 'prod-api-gateway · apps/api/deps',
  },
  {
    id: 'investigate',
    caption: 'Investigate',
    title: 'Investigate',
    mono: 'investigate',
    body:
      'The investigation agent pulls the call graph from the context agent, traces reachability, checks runtime telemetry and attacker paths.',
    metric: 'Reachable? · Exploitable?',
    sub: 'Call-graph + runtime trace',
  },
  {
    id: 'triage',
    caption: 'Gather triage log',
    title: 'Gather triage log',
    mono: 'triage',
    body:
      'Every query, artifact and inference is written to an append-only log. Humans read it, auditors trust it.',
    metric: '14 queries · 6 sources',
    sub: 'Append-only audit trail',
  },
  {
    id: 'root-cause',
    caption: 'Identify root cause',
    title: 'Identify root cause',
    mono: 'root.cause',
    body:
      'CIRA names the owning service, the responsible commit, and the minimal change that remediates.',
    metric: 'github/auth-svc · L218',
    sub: 'Owner: @platform-sec',
  },
  {
    id: 'outcome',
    caption: 'Decide the outcome',
    title: 'Decide the outcome',
    mono: 'decide',
    body:
      'Close as false positive, close with risk-accepted rationale, or escalate a concrete remediation to the code owner.',
    metric: '3 outcomes',
    sub: 'False positive · Risk-accepted · Escalate',
  },
];

const PIPELINE_OUTCOMES = [
  {
    key: 'fp',
    label: 'False positive',
    detail: 'Auto-closed · reasoned · logged',
    variant: 'mint',
  },
  {
    key: 'risk',
    label: 'Risk accepted',
    detail: 'Not exploitable / not reachable',
    variant: 'sand',
  },
  {
    key: 'escalate',
    label: 'Escalate to owner',
    detail: 'PR drafted · routed to @platform-sec',
    variant: 'ink',
  },
];

const OUTCOME_COLUMNS = [
  {
    code: 'fp',
    label: 'False positive',
    swatch: 'mint',
    pct: '64%',
    body:
      'Close findings that are not well founded so triage stays accurate and queues do not fill with noise.',
  },
  {
    code: 'ra',
    label: 'Risk accepted',
    swatch: 'sand',
    pct: '21%',
    body:
      'Not reachable or acceptable compensating controls. Auto-closed with rationale. Re-opens on change.',
  },
  {
    code: 'esc',
    label: 'Escalate to owner',
    swatch: 'ink',
    pct: '15%',
    body: 'Full triage log and verified priority. Draft PR and associated ticket.',
  },
];

const TRIAGE_FEATURES = [
  {
    title: 'Replayable',
    body: 'Reproduce any decision from the log alone.',
    iconSrc: '/images/features/icon_replay.svg',
  },
  {
    title: 'Signed',
    body: 'Append-only. Hash-chained. Tamper-evident.',
    iconSrc: '/images/features/icon_signed.svg',
  },
  {
    title: 'Exportable',
    body: 'SOC 2 / ISO evidence bundles, not screenshots.',
    iconSrc: '/images/features/icon_export.svg',
  },
  {
    title: 'Human-Override',
    body: 'One click reopens with your rationale.',
    iconSrc: '/images/features/icon_click.svg',
  },
];

const AGENT_ROSTER = [
  {
    name: 'Triage',
    role: 'Verifies findings & decides disposition',
    handoff: '← CORRELATION',
    status: 'live',
    swatch: 'triage',
    highlight: true,
  },
  {
    name: 'Context',
    role: 'Builds the asset & service graph',
    handoff: 'GRAPH',
    status: 'live',
    swatch: 'context',
    highlight: false,
  },
  {
    name: 'Correlation',
    role: 'Deduplicates & prioritizes findings',
    handoff: '↔ CONTEXT',
    status: 'live',
    swatch: 'correlation',
    highlight: false,
  },
  {
    name: 'Reporting',
    role: 'Briefings, evidence & exports',
    handoff: '\u2013',
    status: 'soon',
    swatch: 'reporting',
    highlight: false,
  },
  {
    name: 'Remediation',
    role: 'Drafts the fix and routes the PR',
    handoff: '← TRIAGE',
    status: 'soon',
    swatch: 'remediation',
    highlight: false,
  },
  {
    name: 'Policy',
    role: 'Thresholds, gates & compliance',
    handoff: '\u2013',
    status: 'roadmap',
    swatch: 'policy',
    highlight: false,
  },
];

const METRICS = [
  {
    n: '95%',
    label: 'noise reduction',
    sub: 'Prioritization, grouping, validation',
    iconSrc: '/images/features/icon_agent.svg',
  },
  {
    n: '4m 12s',
    label: 'median time to outcome',
    sub: 'End-to-end',
    iconSrc: '/images/features/icon_time.svg',
  },
  {
    n: '11×',
    label: 'throughput vs. human baseline',
    sub: 'Same accuracy',
    iconSrc: '/images/features/icon_code.svg',
  },
  {
    n: '0',
    label: 'false-closed findings on audit',
    sub: '20,000+ triaged',
    iconSrc: '/images/features/icon_audit.svg',
  },
];

const FLOW_ROTATION_MS = 3200;

const SolutionAgentsPage = () => {
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [pointerOverPipeline, setPointerOverPipeline] = useState(false);
  const [focusWithinPipeline, setFocusWithinPipeline] = useState(false);

  const activeStage = WORKFLOW_STAGES[activeStageIndex];
  const pauseAdvance = pointerOverPipeline || focusWithinPipeline;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return undefined;
    if (pauseAdvance) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveStageIndex((i) => (i + 1) % WORKFLOW_STAGES.length);
    }, FLOW_ROTATION_MS);

    return () => window.clearInterval(intervalId);
  }, [pauseAdvance]);

  const handlePipelineBlurCapture = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setFocusWithinPipeline(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={CANONICAL_URL} />
      </Helmet>
      <SeoSocialTags
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        url={CANONICAL_URL}
      />

      <main className="page page--light solution-child-page solution-agents-page">
        <section
          className="page-hero page-hero--dark page-hero--split"
          aria-labelledby="solution-agents-hero-heading"
        >
          <div className="page-container">
            <div className="page-hero--split__grid">
              <div className="page-hero--split__copy">
                <p className="page-hero__eyebrow">
                  CIRA · Circumvent Investigation and Remediation Agents
                </p>
                <h1 id="solution-agents-hero-heading" className="page-hero__title">
                  Closes the loop <em>on every</em> finding.
                </h1>
                <p className="page-hero__lead">
                  CIRA is a team of autonomous agents. It takes a correlated, prioritized finding,
                  investigates root cause against your actual code and runtime, and posts a verdict:
                  false positive, risk accepted, or a precise remediation routed to the code owner.
                </p>
                <p>
                  <Link
                    to="/solutions"
                    className="solution-child-page__back solution-child-page__back--hero"
                  >
                    ← All solutions
                  </Link>
                </p>
              </div>
              <div
                className="page-hero--split__media solution-child-page__hero-media"
                aria-hidden="true"
              >
                <img
                  className="solution-child-page__hero-img"
                  src={CIRA_HERO_ILLUSTRATION}
                  alt=""
                  loading="eager"
                  decoding="async"
                  width={764}
                  height={736}
                />
              </div>
            </div>
          </div>
        </section>

        <section
          className="page-section page-section--light"
          aria-labelledby="solution-agents-pipeline-heading"
        >
          <div className="page-container page-container--stack">
            <div className="section-panel__header section-panel__header--spaced">
              <h2 id="solution-agents-pipeline-heading" className="section-panel__title">
                The pipeline, in plain sight.
              </h2>
              <p className="section-panel__intro">From finding to outcome, end-to-end.</p>
            </div>

            <div
              className="agents-pipeline"
              onPointerEnter={() => setPointerOverPipeline(true)}
              onPointerLeave={() => setPointerOverPipeline(false)}
              onFocusCapture={() => setFocusWithinPipeline(true)}
              onBlurCapture={handlePipelineBlurCapture}
            >
              <p className="agents-pipeline__sr-hint">
                Pipeline stages auto-advance. Hover or focus to pause; click any stage to select it.
              </p>

              <div className="agents-pipeline__stepper-wrap">
                <SolutionsWorkflowStepper
                  theme="light"
                  titleId="solution-agents-workflow-visual"
                  showEyebrow={false}
                  showTitle={false}
                  includeSrTitle={false}
                  titleText="From finding to outcome, end-to-end."
                  activeStepIndex={activeStageIndex}
                  onActiveStepChange={setActiveStageIndex}
                />
              </div>

              <div className="agents-pipeline__detail solution-child-page__card">
                <div className="agents-pipeline__detail-copy">
                  <p className="agents-pipeline__stage-meta">
                    Stage {String(activeStageIndex + 1).padStart(2, '0')} of {WORKFLOW_STAGES.length}
                  </p>
                  <h3 className="agents-pipeline__stage-title">{activeStage.title}</h3>
                  <p className="agents-pipeline__stage-body">{activeStage.body}</p>
                </div>
                <div className="agents-pipeline__signal">
                  <div className="agents-pipeline__signal-kicker">Signal</div>
                  <div className="agents-pipeline__signal-metric">{activeStage.metric}</div>
                  <div className="agents-pipeline__signal-sub">{activeStage.sub}</div>
                  {activeStageIndex === WORKFLOW_STAGES.length - 1 && (
                    <div className="agents-pipeline__outcomes">
                      {PIPELINE_OUTCOMES.map((o) => (
                        <div
                          key={o.key}
                          className={`agents-pipeline__outcome-row agents-pipeline__outcome-row--${o.variant}`}
                        >
                          <span className="agents-pipeline__outcome-label">{o.label}</span>
                          <span className="agents-pipeline__outcome-detail">{o.detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="page-section page-section--white agents-outcomes"
          aria-labelledby="solution-agents-outcomes-heading"
        >
          <div className="page-container page-container--stack">
            <div className="section-panel__header section-panel__header--spaced">
              <h2 id="solution-agents-outcomes-heading" className="section-panel__title">
                Every finding is actioned, no finding remains open.
              </h2>
            </div>
            <div className="agents-outcomes__grid" role="list">
              {OUTCOME_COLUMNS.map((col) => (
                <div
                  key={col.code}
                  className={`agents-outcomes__cell agents-outcomes__cell--${col.swatch}`}
                  role="listitem"
                >
                  <div className="agents-outcomes__label">
                    <span className="agents-outcomes__swatch" aria-hidden="true" />
                    {col.label}
                  </div>
                  <div className="agents-outcomes__pct">{col.pct}</div>
                  <p className="agents-outcomes__body">{col.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="page-section page-section--dark agents-log"
          aria-labelledby="solution-agents-log-heading"
        >
          <div className="page-container">
            <div className="agents-log__grid">
              <div className="agents-log__copy">
                <h2 id="solution-agents-log-heading" className="agents-log__title">
                  Reasoning you can audit.
                </h2>
                <p className="agents-log__lead">
                  Every step CIRA takes is appended to a tamper-evident log. That includes queries issued,
                  sources consulted, and inferences drawn. Replayable, exportable as compliance evidence, and
                  the single source of truth your team reviews.
                </p>
                <ul className="agents-log__features">
                  {TRIAGE_FEATURES.map((feature) => (
                    <li key={feature.title} className="agents-log__feature">
                      <img
                        className="agents-log__feature-icon"
                        src={feature.iconSrc}
                        alt=""
                        width={40}
                        height={40}
                        loading="lazy"
                        decoding="async"
                      />
                      <h3 className="agents-log__feature-title">{feature.title}</h3>
                      <p className="agents-log__feature-body">{feature.body}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="agents-log__media">
                <figure className="agents-log__video-shell">
                  <div className="agents-log__video-cap">
                    <span className="agents-log__video-cap-dot" aria-hidden="true" />
                    <span className="agents-log__video-cap-title">
                      Lambda Environment Variables Free of Hardcoded Secrets
                    </span>
                  </div>
                  <video
                    className="agents-log__video"
                    src={TRIAGE_LOG_VIDEO_SRC}
                    controls
                    playsInline
                    preload="metadata"
                    aria-label="Recorded walkthrough of an audited CIRA triage log"
                  >
                    Your browser does not support embedded video.
                  </video>
                </figure>
              </div>
            </div>
          </div>
        </section>

        <section className="page-section page-section--white" aria-labelledby="solution-agents-roster-heading">
          <div className="page-container page-container--stack">
            <div className="page-section__header">
              <h2 id="solution-agents-roster-heading">CIRA is a team.</h2>
              <p className="solution-child-page__section-dek">
                Circumvent agents are specialists with narrow, testable jobs. Each one hands the next a clean
                interface. Each CIRA agent has shipped or is shipping this year.
              </p>
            </div>
            <div className="agents-roster__table-wrap">
              <table className="agents-roster__table">
                <colgroup>
                  <col className="agents-roster__col agents-roster__col--id" />
                  <col className="agents-roster__col agents-roster__col--agent" />
                  <col className="agents-roster__col agents-roster__col--role" />
                  <col className="agents-roster__col agents-roster__col--handoff" />
                  <col className="agents-roster__col agents-roster__col--status" />
                </colgroup>
                <thead>
                  <tr className="agents-roster__row agents-roster__row--head">
                    <th scope="col">ID</th>
                    <th scope="col">AGENT</th>
                    <th scope="col">ROLE</th>
                    <th scope="col">HANDOFF</th>
                    <th scope="col">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {AGENT_ROSTER.map((a, i) => (
                    <tr
                      key={a.name}
                      className={`agents-roster__row${a.highlight ? ' agents-roster__row--highlight' : ''}${
                        a.status === 'roadmap' ? ' agents-roster__row--muted' : ''
                      }`}
                    >
                      <td className="agents-roster__mono">{String(i + 1).padStart(2, '0')}</td>
                      <td>
                        <span className="agents-roster__name-cell">
                          <span
                            className={`agents-roster__swatch agents-roster__swatch--${a.swatch}`}
                            aria-hidden="true"
                          />
                          <span className="agents-roster__agent-name">{a.name}</span>
                        </span>
                      </td>
                      <td className="agents-roster__role">{a.role}</td>
                      <td className="agents-roster__mono">{a.handoff}</td>
                      <td className={`agents-roster__status agents-roster__status--${a.status}`}>
                        ● {a.status.toUpperCase()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section
          className="page-section page-section--pixel-noise agents-metrics"
          aria-label="CIRA impact metrics"
        >
          <div className="page-container">
            <div className="agents-metrics__grid" role="list">
              {METRICS.map((s) => (
                <div key={s.label} className="agents-metrics__cell" role="listitem">
                  <div className="agents-metrics__value-row">
                    <img
                      className="agents-metrics__icon"
                      src={s.iconSrc}
                      alt=""
                      width={56}
                      height={56}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="agents-metrics__n">{s.n}</div>
                  </div>
                  <div className="agents-metrics__label">{s.label}</div>
                  {s.sub ? <div className="agents-metrics__sub">{s.sub}</div> : null}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="page-section page-section--white solution-child-page__cta-section solution-child-page__cta-section--image"
          aria-labelledby="solution-agents-cta-heading"
          style={NEXT_STEPS_SECTION_STYLE}
        >
          <div className="page-container page-container--stack">
            <div className="page-section__header">
              <h2 id="solution-agents-cta-heading">
                Let CIRA take <em className="agents-cta__accent">the queue</em> off your hands.
              </h2>
              <p className="solution-child-page__cta-lead">
                Onboard with your existing scanners in under a day. Read-only first week, full autonomy when
                you&apos;re ready.
              </p>
              <p className="solution-child-page__cta-actions">
                <a href="/#contact" className="btn btn-solid">
                  Request Access
                </a>
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default SolutionAgentsPage;
